import React, {FC, useEffect, useRef, useState} from 'react'
import {useIntl} from 'react-intl'
import {getFirstInvalidFile, INVALID_FILE_TYPE_MESSAGE} from '../utils/imageValidation'
import {fileOf} from '../utils/productImages'
import '../../../stylesheet/productImages.css'

type Props = {
  /** The tiles, main image first (see utils/productImages). */
  images: any[]
  onChange: (images: any[]) => void
  /** Called instead of onChange when an image is removed, for an owner that tracks deletions. */
  onRemove?: (index: number, remaining: any[]) => void
  /** An error from the owner, shown where the picker's own errors are. */
  error?: string
  children?: React.ReactNode
}

const SortableImages: FC<Props> = ({images, onChange, onRemove, error: ownerError, children}) => {
  const {formatMessage, locale} = useIntl()
  const rtl = locale.startsWith('ar')
  const previews = useRef(new Map<File, string>())
  const [dragOver, setDragOver] = useState<number | null>(null)
  const [dragPosition, setDragPosition] = useState<{index: number; x: number; y: number} | null>(null)
  const dragFrom = useRef<number | null>(null)
  const dragTarget = useRef<number | null>(null)
  const dragStart = useRef({x: 0, y: 0})
  const didDrag = useRef(false)
  const listRef = useRef<HTMLDivElement>(null)
  const slots = useRef<Array<{left: number; top: number; width: number; height: number}>>([])
  const listOrigin = useRef({left: 0, top: 0})
  const itemKeys = useRef(new WeakMap<object, number>())
  const nextItemKey = useRef(0)
  const [preview, setPreview] = useState('')
  const [error, setError] = useState('')

  // One preview URL per picked file, kept for as long as the file is in the list: a
  // reorder must not reload every tile, nor leave an open preview on a revoked URL.
  const srcFor = (item: any): string => {
    const file = fileOf(item)
    if (!file) return item?.image || item || ''
    if (!previews.current.has(file)) previews.current.set(file, URL.createObjectURL(file))
    return previews.current.get(file)!
  }

  useEffect(() => {
    const listed = new Set(images.map(fileOf))
    previews.current.forEach((url, file) => {
      if (!listed.has(file)) {
        URL.revokeObjectURL(url)
        previews.current.delete(file)
      }
    })
  }, [images])

  useEffect(() => {
    const urls = previews.current
    return () => {
      urls.forEach((url) => URL.revokeObjectURL(url))
      urls.clear()
    }
  }, [])

  const reorder = (from: number, to: number) => {
    if (from === to || to < 0 || to >= images.length) return
    const ordered = [...images]
    ordered.splice(to, 0, ordered.splice(from, 1)[0])
    onChange(ordered)
  }

  const targetIndex = (x: number, y: number): number | null => {
    if (!slots.current.length) return dragFrom.current
    let nearest = 0
    let distance = Infinity
    slots.current.forEach((slot, index) => {
      const dx = x - slot.left - slot.width / 2
      const dy = y - slot.top - slot.height / 2
      const squaredDistance = dx * dx + dy * dy
      if (squaredDistance < distance) { distance = squaredDistance; nearest = index }
    })
    return nearest
  }

  const keyFor = (item: any): string | number => {
    if (item && typeof item === 'object') {
      if (!itemKeys.current.has(item)) itemKeys.current.set(item, nextItemKey.current++)
      return itemKeys.current.get(item)!
    }
    return String(item)
  }

  const tileStyle = (index: number): React.CSSProperties | undefined => {
    if (!dragPosition) return undefined
    if (index === dragPosition.index) {
      return {transform: `translate3d(${dragPosition.x}px, ${dragPosition.y}px, 0) scale(1.04)`}
    }
    const from = dragPosition.index
    const to = dragOver ?? from
    const destination = from < to && index > from && index <= to ? index - 1
      : from > to && index >= to && index < from ? index + 1 : index
    if (destination === index || !slots.current[index] || !slots.current[destination]) return undefined
    return {transform: `translate3d(${slots.current[destination].left - slots.current[index].left}px, ${slots.current[destination].top - slots.current[index].top}px, 0)`}
  }

  const resetDrag = () => {
    dragFrom.current = null
    dragTarget.current = null
    setDragOver(null)
    setDragPosition(null)
  }

  const finishDrag = (x: number, y: number) => {
    const to = targetIndex(x, y) ?? dragTarget.current
    if (didDrag.current && dragFrom.current !== null && to !== null) reorder(dragFrom.current, to)
    resetDrag()
    window.setTimeout(() => { didDrag.current = false }, 0)
  }

  const remove = (index: number) => {
    if (images.length <= 1) return
    const remaining = images.filter((_, i) => i !== index)
    if (onRemove) onRemove(index, remaining)
    else onChange(remaining)
  }

  const addFiles = (files: FileList | null) => {
    if (!files?.length) return
    if (getFirstInvalidFile(files)) {
      setError(INVALID_FILE_TYPE_MESSAGE)
      return
    }
    if (images.length + files.length > 10) {
      setError(formatMessage({id: 'upload_images_limit_prod_err'}))
      return
    }
    setError('')
    onChange([...images, ...Array.from(files)])
  }

  return <div className='card default-image-box'>
    <div className='card-body'>
      <h3 className='card-title mb-8'>{formatMessage({id: 'more_images'})}</h3>
      <label className='upload-from-box border mb-3 px-3 py-2 rounded-2 d-block cursor-pointer'>
        {formatMessage({id: 'upload_from_computer'})}
        <input type='file' multiple accept='image/jpeg,image/png,image/webp' className='visually-hidden'
          onChange={(event) => { addFiles(event.target.files); event.target.value = '' }} />
      </label>
      <p className='text-gray-600'>{formatMessage({id: 'upload_images_limit_prod'})}</p>
      {(error || ownerError) && <p className='text-danger' role='alert' style={{whiteSpace: 'pre-line'}}>{error || ownerError}</p>}
      {images.length > 1 && <p className='text-gray-600'>{formatMessage({id: 'reorder_images_hint'})}</p>}
      <div className='product-image-sort-list' ref={listRef}>
        {dragPosition && dragOver !== null && dragOver !== dragPosition.index && slots.current[dragOver] &&
          <div className='product-image-drop-slot' style={{
            left: slots.current[dragOver].left - listOrigin.current.left,
            top: slots.current[dragOver].top - listOrigin.current.top,
            width: slots.current[dragOver].width,
            height: slots.current[dragOver].height,
          }} />}
        {images.map((item, index) => <div key={keyFor(item)}
          className={`product-image-sort-item ${dragPosition?.index === index ? 'product-image-sort-dragging' : ''} ${dragPosition && tileStyle(index) && index !== dragPosition.index ? 'product-image-sort-shifting' : ''}`}
          style={tileStyle(index)}
          data-image-index={index}
          onDragStart={(event) => event.preventDefault()}
          onPointerDown={(event) => {
            if (event.target instanceof Element && event.target.closest('.product-image-remove')) return
            if (event.pointerType === 'mouse' && event.button !== 0) return
            dragFrom.current = index
            dragTarget.current = index
            dragStart.current = {x: event.clientX, y: event.clientY}
            didDrag.current = false
            slots.current = Array.from(listRef.current?.querySelectorAll<HTMLElement>('[data-image-index]') || []).map((tile) => tile.getBoundingClientRect())
            const origin = listRef.current?.getBoundingClientRect()
            listOrigin.current = {left: origin?.left || 0, top: origin?.top || 0}
          }}
          onPointerMove={(event) => {
            if (dragFrom.current === null) return
            // The pointer is only captured once a drag starts, so a press let go outside the
            // tile never reaches onPointerUp. No button held means that press is over.
            if (event.buttons === 0) { resetDrag(); return }
            if (Math.hypot(event.clientX - dragStart.current.x, event.clientY - dragStart.current.y) < 6) return
            if (!didDrag.current) {
              didDrag.current = true
              event.currentTarget.setPointerCapture(event.pointerId)
              setDragOver(dragFrom.current)
            }
            setDragPosition({index: dragFrom.current, x: event.clientX - dragStart.current.x, y: event.clientY - dragStart.current.y})
            const to = targetIndex(event.clientX, event.clientY)
            if (to !== null) { dragTarget.current = to; setDragOver(to) }
          }}
          onPointerUp={(event) => finishDrag(event.clientX, event.clientY)}
          onPointerCancel={resetDrag}>
          <div className='product-image-sort-handle'
            role='button' tabIndex={0} aria-label={formatMessage({id: 'image_move'}, {position: index + 1})}
            onKeyDown={(event) => {
              // Left and right follow the reading direction: in Arabic the next image is to the left.
              const steps: Record<string, number> = {ArrowUp: -1, ArrowDown: 1, ArrowLeft: rtl ? 1 : -1, ArrowRight: rtl ? -1 : 1}
              const step = steps[event.key]
              if (step) { event.preventDefault(); reorder(index, index + step) }
            }}>☰</div>
          <button type='button' className='product-image-preview' onClick={() => { if (!didDrag.current) setPreview(srcFor(item)) }}
            aria-label={formatMessage({id: 'image_preview'}, {position: index + 1})}>
            <img src={srcFor(item)} alt={formatMessage({id: 'image_alt'}, {position: index + 1})} draggable={false} />
          </button>
          {index === 0 && <span className='product-image-main-label'>{formatMessage({id: 'default_image'})}</span>}
          <button type='button' className='product-image-remove' onClick={() => remove(index)}
            aria-label={formatMessage({id: 'image_remove'}, {position: index + 1})} disabled={images.length <= 1}>×</button>
        </div>)}
      </div>
      {children}
      {preview && <div className='product-image-preview-overlay' onClick={() => setPreview('')} role='presentation'>
        <button type='button' aria-label={formatMessage({id: 'image_preview_close'})} onClick={() => setPreview('')}>×</button>
        <img src={preview} alt={formatMessage({id: 'image_preview_alt'})} />
      </div>}
    </div>
  </div>
}

export default SortableImages
